import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit3, Eye, FileText, Filter, PlusCircle, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import api from "@/lib/api";
import { money, formatDate } from "@/lib/format";
import { useAuth, isManagerOrAdmin } from "@/lib/auth.jsx";
import { getApiErrorMessage } from "@/lib/apiError";

function quoteCustomerName(quote) {
    const customer = quote.customer_snapshot;
    if (!customer) return "—";
    return `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || "—";
}

function quoteHomeLabel(quote) {
    return quote.home?.model_name || "No home selected";
}

function quoteHomeSubLabel(quote) {
    return [quote.home?.manufacturer, quote.home?.series].filter(Boolean).join(" · ");
}

function belongsToCurrentUser(quote, user) {
    if (!user?.id) return false;
    return quote.associate_id === user.id;
}

export default function Quotes() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [quotes, setQuotes] = useState([]);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [deletingId, setDeletingId] = useState(null);

    const load = () => {
        const params = {};
        const isLookup = q.trim().length > 0;
        const canViewAllByDefault = isManagerOrAdmin(user);
        if (isLookup) params.q = q.trim();
        if (status && status !== "all") params.status = status;

        api.get("/quotes", { params }).then((r) => {
            const data = r.data || [];
            setQuotes(isLookup || canViewAllByDefault ? data : data.filter((quote) => belongsToCurrentUser(quote, user)));
        });
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, user?.id, user?.role]);

    const viewQuote = (quote) => {
        navigate(`/quotes/${quote.id}`);
    };

    const editQuote = (quote) => {
        navigate(`/quotes/${quote.id}/edit`);
    };

    const deleteQuote = async (quote) => {
        const confirmed = window.confirm(
            `Delete quote ${quote.quote_number || "this quote"} for ${quoteCustomerName(quote)}?\n\nThis cannot be undone. If this quote was shared, the public link will stop working.`
        );
        if (!confirmed) return;

        setDeletingId(quote.id);
        try {
            await api.delete(`/quotes/${quote.id}`);
            toast.success("Quote deleted");
            await load();
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Unable to delete quote"));
        } finally {
            setDeletingId(null);
        }
    };

    const listLabel = q.trim()
        ? "Lookup results across dealership quotes"
        : isManagerOrAdmin(user)
            ? "Dealership quote workspace"
            : "Your quote workspace";

    return (
        <div className="space-y-6" data-testid="quotes-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl text-slate-900">Quotes</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {listLabel}. Search by quote number, customer, or model to look up dealership quotes.
                    </p>
                </div>
                <Button onClick={() => navigate("/quotes/new")} className="bg-[#0F2A47] hover:bg-[#14385E] text-white" data-testid="quotes-new-quote-button">
                    <PlusCircle className="mr-2 h-4 w-4" /> New Quote
                </Button>
            </div>

            <Card className="rounded-xl border-slate-200 overflow-hidden">
                <div className="flex flex-col md:flex-row md:items-center gap-3 border-b border-slate-100 px-4 py-3">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Lookup quote #, customer, model…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && load()}
                            className="pl-9"
                            data-testid="quotes-search-input"
                        />
                    </div>
                    <div className="flex gap-2">
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger className="w-full md:w-[180px]" data-testid="quotes-status-filter">
                                <SelectValue placeholder="All statuses" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All statuses</SelectItem>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="sent">Sent</SelectItem>
                                <SelectItem value="accepted">Accepted</SelectItem>
                                <SelectItem value="declined">Declined</SelectItem>
                                <SelectItem value="expired">Expired</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button variant="outline" onClick={load} data-testid="quotes-search-button">
                            <Filter className="mr-2 h-4 w-4" /> Apply
                        </Button>
                    </div>
                </div>

                {quotes.length === 0 ? (
                    <div className="p-12 text-center">
                        <FileText className="mx-auto h-10 w-10 text-slate-300" />
                        <h3 className="mt-3 text-sm font-medium text-slate-900">No quotes match</h3>
                        <p className="mt-1 text-sm text-slate-500">
                            Try clearing filters, searching by customer/quote number, or create a new quote.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="md:hidden divide-y divide-slate-100">
                            {quotes.map((quote) => (
                                <div key={quote.id} className="p-4 space-y-3" data-testid={`quotes-card-${quote.id}`}>
                                    <button type="button" className="block text-left w-full" onClick={() => viewQuote(quote)}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="font-mono text-xs text-slate-500 truncate">{quote.quote_number}</div>
                                                <div className="font-semibold text-slate-900 mt-1">{quoteCustomerName(quote)}</div>
                                            </div>
                                            <StatusBadge status={quote.status} />
                                        </div>
                                        <div className="text-sm text-slate-700 mt-2">{quoteHomeLabel(quote)}</div>
                                        {quoteHomeSubLabel(quote) && (
                                            <div className="text-xs text-slate-500">{quoteHomeSubLabel(quote)}</div>
                                        )}
                                        <div className="flex items-center justify-between mt-2 text-sm">
                                            <span className="text-slate-500">{formatDate(quote.quote_date)}</span>
                                            <span className="font-semibold tabular text-slate-900">{money(quote.totals?.grand_total)}</span>
                                        </div>
                                    </button>
                                    <div className="grid grid-cols-3 gap-2">
                                        <Button variant="outline" size="sm" onClick={() => viewQuote(quote)} data-testid={`quotes-view-${quote.id}`}>
                                            <Eye className="mr-1.5 h-3.5 w-3.5" /> View
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => editQuote(quote)} data-testid={`quotes-edit-${quote.id}`}>
                                            <Edit3 className="mr-1.5 h-3.5 w-3.5" /> Edit
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => deleteQuote(quote)}
                                            disabled={deletingId === quote.id}
                                            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                                            data-testid={`quotes-delete-${quote.id}`}
                                        >
                                            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> {deletingId === quote.id ? "..." : "Delete"}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="hidden md:block">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Quote #</TableHead>
                                        <TableHead>Customer</TableHead>
                                        <TableHead>Home</TableHead>
                                        <TableHead>Associate</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Total</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {quotes.map((quote) => (
                                        <TableRow key={quote.id} className="hover:bg-slate-50" data-testid={`quotes-row-${quote.id}`}>
                                            <TableCell className="font-mono text-xs text-slate-700 cursor-pointer" onClick={() => viewQuote(quote)}>{quote.quote_number}</TableCell>
                                            <TableCell>{quoteCustomerName(quote)}</TableCell>
                                            <TableCell>
                                                {quote.home?.model_name ? (
                                                    <div>
                                                        <div className="font-medium">{quote.home?.model_name}</div>
                                                        <div className="text-xs text-slate-500">{quoteHomeSubLabel(quote)}</div>
                                                    </div>
                                                ) : "—"}
                                            </TableCell>
                                            <TableCell className="text-slate-600">{quote.associate_name || "—"}</TableCell>
                                            <TableCell className="text-slate-600">{formatDate(quote.quote_date)}</TableCell>
                                            <TableCell><StatusBadge status={quote.status} /></TableCell>
                                            <TableCell className="text-right tabular font-medium">{money(quote.totals?.grand_total)}</TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button variant="outline" size="sm" onClick={() => viewQuote(quote)} data-testid={`quotes-view-${quote.id}`}>
                                                        <Eye className="mr-1.5 h-3.5 w-3.5" /> View
                                                    </Button>
                                                    <Button variant="outline" size="sm" onClick={() => editQuote(quote)} data-testid={`quotes-edit-${quote.id}`}>
                                                        <Edit3 className="mr-1.5 h-3.5 w-3.5" /> Edit
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => deleteQuote(quote)}
                                                        disabled={deletingId === quote.id}
                                                        className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                                                        data-testid={`quotes-delete-${quote.id}`}
                                                    >
                                                        <Trash2 className="mr-1.5 h-3.5 w-3.5" /> {deletingId === quote.id ? "Deleting…" : "Delete"}
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}
